import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Truck } from "lucide-react";
import SealProgress from "@/components/tracking/SealProgress";
import { fillBlockTokens, useContentBlocks } from "@/lib/contentBlocks";

// Stage order. These values are machinery and match the database exactly.
const STAGES = [
  "pending",
  "ready",
  "ein_acquired",
  "filings_submitted",
  "filings_received",
  "docs_sent",
  "docs_delivered",
] as const;

// Older records may carry these earlier spellings.
const STAGE_ALIASES: Record<string, string> = {
  filed_with_state: "filings_submitted",
  state_processed: "filings_received",
};

// Headings the team can edit live in content blocks. These are the fallbacks.
const STAGE_LABEL: Record<string, string> = {
  pending: "Getting started",
  ready: "Underway",
  ein_acquired: "Tax number in hand",
  filings_submitted: "Filed with the state",
  filings_received: "Approved by the state",
  docs_sent: "On the way to you",
  docs_delivered: "Delivered",
};

/** Plain dates, the way a person writes them. */
function plainDate(value: string | null | undefined): string {
  if (!value) return "";
  const text = String(value);
  const date = text.length === 10 ? new Date(`${text}T12:00:00Z`) : new Date(text);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-US", {
    month: "long", day: "numeric", year: "numeric", timeZone: "UTC",
  });
}

export default function Tracking() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // The team can open a member's tracking page read only from the admin.
  const previewId = params.get("member");
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [record, setRecord] = useState<any>(null);
  const [dates, setDates] = useState<Record<string, string>>({});
  const [formation, setFormation] = useState<any>(null);
  const { blocks } = useContentBlocks(["tracking", "wait"]);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      const columns = "id, first_name, una_name, fulfillment_stage, tracking_number";
      const query = (supabase as any).from("member_records").select(columns);
      const { data: rec } = previewId
        ? await query.eq("id", previewId).maybeSingle()
        : await query.eq("user_id", user.id).maybeSingle();
      setRecord(rec);
      setPreviewing(Boolean(previewId && rec));

      if (rec?.id) {
        const { data: events } = await (supabase as any)
          .from("fulfillment_events")
          .select("to_stage, created_at")
          .eq("member_record_id", rec.id)
          .order("created_at", { ascending: true });
        const map: Record<string, string> = {};
        for (const e of events || []) map[STAGE_ALIASES[e.to_stage] ?? e.to_stage] = e.created_at;
        setDates(map);

        const { data: form } = await (supabase as any)
          .from("formations")
          .select("*")
          .eq("member_record_id", rec.id)
          .maybeSingle();
        setFormation(form);
      }
      setLoading(false);
    };
    void load();
  }, [user, previewId]);

  const raw = String(record?.fulfillment_stage || "pending");
  const stage = STAGE_ALIASES[raw] ?? raw;
  const stageIndex = Math.max(0, STAGES.indexOf(stage as (typeof STAGES)[number]));

  const fedexTracking = formation?.fedex_tracking || record?.tracking_number || "";

  const tokens = useMemo(
    () => ({
      first_name: record?.first_name ?? "",
      una_name: record?.una_name ?? "your Association",
      tracking_number: record?.tracking_number ?? "",
      fedex_tracking: formation?.fedex_tracking ?? record?.tracking_number ?? "",
      ein_scheduled_for: plainDate(formation?.ein_scheduled_for),
      ein_acquired_on: plainDate(formation?.ein_acquired_on),
      ca_submitted_on: plainDate(formation?.ca_submitted_on),
      ca_approved_on: plainDate(formation?.ca_approved_on),
      docs_sent_on: plainDate(dates.docs_sent),
      fedex_delivered_on: plainDate(formation?.fedex_delivered_on),
      packet_received_on: plainDate(formation?.packet_received_on),
    }),
    [record, formation, dates],
  );

  const line = (slug: string) => {
    const body = blocks[slug]?.body_markdown ?? "";
    return fillBlockTokens(body, tokens).trim();
  };

  /** Nothing is ever invented. An empty date sends us to the fallback wording. */
  const lineWithFallback = (slug: string) => {
    const body = blocks[slug]?.body_markdown ?? "";
    const names = [...body.matchAll(/\{\{\s*([a-z_]+)\s*\}\}/g)].map((m) => m[1]);
    const missing = names.some((name) => !String((tokens as Record<string, string>)[name] ?? "").trim());
    if (missing && blocks[`${slug}_fallback`]) return line(`${slug}_fallback`);
    return line(slug);
  };

  const deliveredWaiting = Boolean(formation?.fedex_delivered_on) && !formation?.packet_received_on;
  const stageLine =
    stage === "docs_sent" && deliveredWaiting && blocks.tracking_docs_sent_delivered
      ? line("tracking_docs_sent_delivered")
      : lineWithFallback(`tracking_${stage}`);
  const introLine = line("tracking_intro");
  const heading = line(`tracking_heading_${stage}`) || STAGE_LABEL[stage] || "Underway";
  const when = dates[stage];

  // While you wait cards rotate by day, so the page feels alive without noise.
  const waitCards = useMemo(() => {
    const all = Object.values(blocks).filter((b) => b.kind === "wait");
    if (all.length === 0) return [];
    const start = new Date().getDate() % all.length;
    return [all[start], all[(start + 1) % all.length]].filter(Boolean);
  }, [blocks]);

  if (loading) {
    return (
      <div className="min-h-[100dvh] bg-muted/20 flex items-center justify-center gap-2 text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading your progress
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-muted/20 pb-[env(safe-area-inset-bottom)]">
      <div className="mx-auto w-full max-w-2xl px-4 pb-16 pt-6 sm:pt-14">
        {previewing && (
          <div className="mb-4 rounded-md border border-amber-300/60 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-950/30 dark:text-amber-300">
            Admin preview. This is what this member sees. Nothing here can be changed.
          </div>
        )}

        <SealProgress completed={stageIndex + 1} label={heading} />

        <div className="mt-6 space-y-3">
          <p className="text-center text-sm text-muted-foreground">
            {record?.una_name ? record.una_name : "Your Association"}
          </p>

          <Card>
            <CardContent className="space-y-3 p-5 sm:p-8">
              <h1 className="text-xl font-semibold">{heading}</h1>
              {when && (
                <p className="text-xs text-muted-foreground">{plainDate(when)}</p>
              )}
              {stageLine && (
                <div className="text-sm leading-relaxed text-muted-foreground [&_p]:mb-2">
                  <ReactMarkdown>{stageLine}</ReactMarkdown>
                </div>
              )}
              {introLine && (
                <p className="text-sm leading-relaxed text-muted-foreground">{introLine}</p>
              )}

              {stage === "docs_sent" && fedexTracking && (
                <a
                  className="mt-2 flex items-center gap-2 rounded-md border bg-background px-3 py-2 text-sm hover:bg-muted"
                  href={`https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(fedexTracking)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Truck className="h-4 w-4 text-muted-foreground" />
                  <span className="font-mono">{fedexTracking}</span>
                  <span className="ml-auto text-xs text-muted-foreground">Track it</span>
                </a>
              )}

              {stage === "docs_delivered" && (
                <Button className="mt-2 w-full sm:w-auto" onClick={() => navigate("/journey")}>
                  Enter your member portal
                </Button>
              )}
            </CardContent>
          </Card>

          {waitCards.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-2">
              {waitCards.map((b) => (
                <Card key={b.slug}>
                  <CardContent className="p-4 text-sm leading-relaxed text-muted-foreground [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold [&_h3]:text-foreground [&_p]:mb-0">
                    <ReactMarkdown>{fillBlockTokens(b.body_markdown, tokens)}</ReactMarkdown>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
